import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  login,
  register,
  logout,
  fetchCurrentUser,
  updateProfile,
  changePassword,
  initializeAuth,
  clearError,
  selectUser,
  selectIsAuthenticated,
  selectIsLoading,
  selectIsInitialized,
  selectAuthError,
} from '@/store/authSlice';
import type {
  LoginCredentials,
  RegisterData,
  UpdateProfileData,
  ChangePasswordData,
  User,
  UserRole,
} from '@/types/user.types';
import { ROUTES } from '@/utils/constants';

interface UseAuthReturn {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  handleLogin: (credentials: LoginCredentials) => Promise<void>;
  handleRegister: (data: RegisterData) => Promise<void>;
  handleLogout: () => Promise<void>;
  handleUpdateProfile: (data: UpdateProfileData) => Promise<void>;
  handleChangePassword: (data: ChangePasswordData) => Promise<void>;
  refreshUser: () => Promise<void>;
  clearAuthError: () => void;

  // Helpers
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

export function useAuth(): UseAuthReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Select state from store
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const isInitialized = useAppSelector(selectIsInitialized);
  const error = useAppSelector(selectAuthError);

  // Initialize auth on mount
  useEffect(() => {
    if (!isInitialized) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isInitialized]);

  // Login handler
  const handleLogin = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      const result = await dispatch(login(credentials));

      if (login.fulfilled.match(result)) {
        // Get the redirect path from location state or default to dashboard
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTES.DASHBOARD;
        navigate(from, { replace: true });
      }
    },
    [dispatch, navigate, location.state]
  );

  // Register handler
  const handleRegister = useCallback(
    async (data: RegisterData): Promise<void> => {
      const result = await dispatch(register(data));

      if (register.fulfilled.match(result)) {
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
    },
    [dispatch, navigate]
  );

  // Logout handler
  const handleLogout = useCallback(async (): Promise<void> => {
    await dispatch(logout());
    navigate(ROUTES.LOGIN, { replace: true });
  }, [dispatch, navigate]);

  // Update profile handler
  const handleUpdateProfile = useCallback(
    async (data: UpdateProfileData): Promise<void> => {
      await dispatch(updateProfile(data)).unwrap();
    },
    [dispatch]
  );

  // Change password handler
  const handleChangePassword = useCallback(
    async (data: ChangePasswordData): Promise<void> => {
      await dispatch(changePassword(data)).unwrap();
    },
    [dispatch]
  );

  // Refresh user data
  const refreshUser = useCallback(async (): Promise<void> => {
    await dispatch(fetchCurrentUser());
  }, [dispatch]);

  // Clear auth error
  const clearAuthError = useCallback((): void => {
    dispatch(clearError());
  }, [dispatch]);

  // Check if user has a specific role
  const hasRole = useCallback(
    (roles: UserRole | UserRole[]): boolean => {
      if (!user) return false;

      const rolesArray = Array.isArray(roles) ? roles : [roles];
      return rolesArray.includes(user.role);
    },
    [user]
  );

  // Check if user has a specific permission
  // This is a simplified version - in a real app, you'd have a more complex permission system
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;

      // Admin has all permissions
      if (user.role === 'admin') return true;

      // Define role-based permissions
      const rolePermissions: Record<UserRole, string[]> = {
        admin: ['*'],
        manager: [
          'read:users',
          'write:users',
          'read:reports',
          'write:reports',
          'read:settings',
        ],
        employee: ['read:users', 'read:reports'],
        viewer: ['read:users'],
      };

      const permissions = rolePermissions[user.role] || [];
      return permissions.includes(permission) || permissions.includes('*');
    },
    [user]
  );

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,

    // Actions
    handleLogin,
    handleRegister,
    handleLogout,
    handleUpdateProfile,
    handleChangePassword,
    refreshUser,
    clearAuthError,

    // Helpers
    hasRole,
    hasPermission,
  };
}

// Hook for requiring authentication
export function useRequireAuth(redirectTo: string = ROUTES.LOGIN): void {
  const { isAuthenticated, isInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate(redirectTo, {
        replace: true,
        state: { from: location },
      });
    }
  }, [isAuthenticated, isInitialized, navigate, redirectTo, location]);
}

// Hook for preventing authenticated users from accessing certain pages
export function useRedirectIfAuthenticated(redirectTo: string = ROUTES.DASHBOARD): void {
  const { isAuthenticated, isInitialized } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, redirectTo]);
}

// Hook for role-based access control
export function useRequireRole(allowedRoles: UserRole | UserRole[]): boolean {
  const { user, hasRole, isInitialized } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isInitialized && user && !hasRole(allowedRoles)) {
      navigate(ROUTES.FORBIDDEN, { replace: true });
    }
  }, [user, isInitialized, hasRole, allowedRoles, navigate]);

  return hasRole(allowedRoles);
}

export default useAuth;
